import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { CardsContainer } from 'components/cards';
import {
  Title,
  CardsSubContainer,
  ButtonContainer,
  Button,
  ButtonLink,
  BillingCard,
  UploadLoader
} from './style';
import { Inhalt, Upload } from './Sections';
import { useTranslation } from 'hooks';

function BillingTab({
  monthlyReport,
  vatList,
  language,
  onVerify,
  toggleMonthChange,
  syncing,
  html,
  isVerified,
  placement,
  onSave,
  reset,
  month,
  year,
  isMU = false,
  onSaveMonthlyReport
}) {
  const { t } = useTranslation();
  const { id: monthlyReportId, billingStatus } = monthlyReport || {};
  const { id: placementId, travelExpenseManager, feeBilling } = placement || {};

  const [reports, setReports] = useState({});
  const [errors, setErrors] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);

  useEffect(() => {
    reset && reset();
    handleAbort(false);
  }, [toggleMonthChange, placementId]); // re-run on placement change

  const handleVerify = () => {
    if (reports?.invoice && onVerify) {
      onVerify({
        id: monthlyReportId,
        language,
        invoiceReport: reports.invoice
      });
    }
  };

  const handleSave = () => {
    if (isMU) {
      onSaveMonthlyReport(placementId, {
        ...monthlyReport,
        billingStatus: '200',
        withTimeStamp: true
      });
      return;
    }

    if (travelExpenseManager === '200' && !reports?.expenses && !showConfirmation) {
      setShowConfirmation(true);
      return;
    }
    setShowConfirmation(false);
    onSave &&
      onSave({
        id: monthlyReportId,
        expensesReport: reports?.expenses,
        placementId,
        month,
        year
      });
  };

  const handleAbort = (doReset = true) => {
    if (!syncing) {
      setReports({});
      setErrors({});
      if (document.getElementById('invoice')) document.getElementById('invoice').value = '';
      if (document.getElementById('expenses')) document.getElementById('expenses').value = '';
      reset && doReset && reset();
    }
  };

  return (
    <>
      <CardsContainer $jc="space-between">
        <CardsSubContainer size="4.5">
          <Title>{isMU ? t('title.payrollContent') : t('title.invoiceContent')}</Title>
          <BillingCard>
            <Inhalt
              feeBilling={feeBilling}
              language={language}
              monthlyReport={monthlyReport}
              vatList={vatList}
              isMU={isMU}
            />
          </BillingCard>
        </CardsSubContainer>

        <CardsSubContainer size="5.5">
          <Title>{isMU ? t('title.payrollUpload') : t('title.invoiceUpload')}</Title>
          <BillingCard>
            <UploadLoader loading={syncing}>
              <Upload
                errors={errors}
                reports={reports}
                billingStatus={billingStatus}
                html={html}
                isVerified={isVerified}
                setReports={setReports}
                setErrors={setErrors}
                travelExpense={travelExpenseManager}
                showConfirmation={showConfirmation}
                setShowConfirmation={setShowConfirmation}
                isMU={isMU}
              />
            </UploadLoader>
          </BillingCard>
        </CardsSubContainer>
      </CardsContainer>

      {monthlyReportId && billingStatus === '100' && (
        <ButtonContainer>
          <Button
            color="primary"
            disabled={!isMU && !isVerified}
            disabledColor="gray"
            label={t('label.submit')}
            onClick={handleSave}
          />
          {reports?.invoice && (
            <>
              <Button
                color="primary"
                disabled={isVerified}
                label={t('label.verify')}
                onClick={handleVerify}
              />
              <Button label={t('label.abort')} color='primary' variant='text' onClick={handleAbort} />
            </>
          )}
        </ButtonContainer>
      )}
    </>
  );
}

BillingTab.propTypes = {
  monthlyReport: PropTypes.object,
  vatList: PropTypes.array,
  language: PropTypes.string,
  onVerify: PropTypes.func.isRequired,
  toggleMonthChange: PropTypes.bool,
  syncing: PropTypes.bool,
  html: PropTypes.string,
  isVerified: PropTypes.bool,
  placement: PropTypes.object,
  month: PropTypes.number,
  year: PropTypes.number,
  onSave: PropTypes.func.isRequired,
  reset: PropTypes.func.isRequired,
  onSaveMonthlyReport: PropTypes.func,
  isMU: PropTypes.bool
};

export default BillingTab;
